import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, Eye } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { gbp, fmtDate, ORDER_STATUS_LABELS, ORDER_STATUSES, PAYMENT_STATUS_LABELS } from '../lib/shop';
import { PageHead, Modal, Field, inp } from './components';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ status: '', tracking: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get('/admin/orders', { params });
      setOrders(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [status, q]);

  const open = (o) => { setSel(o); setForm({ status: o.status, tracking: o.tracking || '', note: '' }); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${sel.id}`, form);
      toast.success('Order updated');
      if (form.status === 'dispatched' && sel.status !== 'dispatched') toast.success('Dispatch email sent to customer');
      if (['cancelled', 'refunded'].includes(form.status) && data.stock_restored) toast.success('Stock restored to inventory');
      setSel(data);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const quickPay = async (status, label) => {
    if (!sel?.payment) { toast.error('No payment record for this order'); return; }
    if (!window.confirm(`${label} — order ${sel.order_number}?`)) return;
    try {
      await api.post(`/admin/payments/${sel.payment.id}/status`, { status });
      toast.success(status === 'successful' ? 'Payment confirmed — order paid, stock finalised, customer emailed' : label);
      setSel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const quickCancel = async () => {
    if (!window.confirm(`Cancel order ${sel.order_number}? Any deducted stock is restored.`)) return;
    try {
      await api.put(`/admin/orders/${sel.id}`, { status: 'cancelled' });
      toast.success('Order cancelled');
      setSel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-orders">
      <PageHead title="Orders" sub="Search, review and progress every order." />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 border hairline bg-white px-3 py-2.5 max-w-xs flex-1">
          <Search className="w-4 h-4 text-[#8A8F95]" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order # or customer…" className="flex-1 text-sm focus:outline-none" />
        </div>
        <select data-testid="orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="border hairline bg-white px-3 py-2.5 text-xs font-mono2 uppercase focus:outline-none">
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b hairline">
              {['Order', 'Customer', 'Items', 'Payment', 'Status', 'Total', 'Date', ''].map((h) => (
                <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b hairline last:border-0 hover:bg-[#FAFAF8]">
                <td className="p-3 font-mono2 font-semibold">{o.order_number}</td>
                <td className="p-3">{o.customer_email}</td>
                <td className="p-3 font-mono2 text-xs">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-0.5">{o.payment ? (PAYMENT_STATUS_LABELS[o.payment.status] || o.payment.status) : '—'}</span></td>
                <td className="p-3"><span className="font-mono2 text-[10px] uppercase tracking-wider border hairline px-2 py-0.5" data-testid={`order-status-${o.id}`}>{ORDER_STATUS_LABELS[o.status]}</span></td>
                <td className="p-3 font-mono2">{gbp(o.total)}</td>
                <td className="p-3 text-xs text-[#8A8F95]">{fmtDate(o.created_at)}</td>
                <td className="p-3"><button data-testid={`order-view-${o.id}`} onClick={() => open(o)} className="p-1.5 hover:bg-[#F4F4F0]"><Eye className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#8A8F95]">No orders match</td></tr>}
          </tbody>
        </table>
      </div>

      {sel && (
        <Modal title={`Order ${sel.order_number}`} onClose={() => setSel(null)} wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2">Items</p>
              <div className="space-y-2">
                {sel.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm border-b hairline pb-2">
                    <span className="font-display font-bold uppercase">{it.name} <span className="text-[#8A8F95]">{it.strength}</span> × {it.quantity}</span>
                    <span className="font-mono2">{gbp(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <p className="font-mono2 text-sm font-bold mt-3 text-right">Total {gbp(sel.total)}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mt-5 mb-2">Delivery</p>
              <p className="text-sm">{sel.shipping_address?.name}<br />{sel.shipping_address?.line1} {sel.shipping_address?.line2}<br />{sel.shipping_address?.city} {sel.shipping_address?.postcode}<br />{sel.shipping_address?.country}</p>
              <p className="font-mono2 text-xs text-[#8A8F95] mt-2">{sel.shipping_method} · {sel.customer_email}</p>
              {sel.payment && (
                <>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mt-5 mb-2">Payment</p>
                  <p className="font-mono2 text-xs">{sel.payment.payment_method} · {PAYMENT_STATUS_LABELS[sel.payment.status]}{sel.payment.transaction_hash ? ` · tx ${sel.payment.transaction_hash.slice(0, 18)}…` : ''}</p>
                </>
              )}
            </div>
            <div className="space-y-4">
              {sel.payment && (
                <div className="border hairline p-3 bg-[#F4F4F0]">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2">
                    Payment actions — {sel.payment.payment_method} · {PAYMENT_STATUS_LABELS[sel.payment.status] || sel.payment.status}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button data-testid="order-mark-received" disabled={['successful', 'confirmed'].includes(sel.payment.status)}
                      onClick={() => quickPay('successful', 'Mark payment received')}
                      className="bg-[#1F8A58] text-white px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:opacity-90 disabled:opacity-40 transition-opacity">
                      Mark payment received
                    </button>
                    <button data-testid="order-mark-not-received" onClick={() => quickPay('failed', 'Mark payment not received')}
                      className="border hairline bg-white px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#0B0F12]">
                      Not received
                    </button>
                    <button data-testid="order-manual-review" onClick={() => quickPay('manual_review', 'Move to manual review')}
                      className="border hairline bg-white px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:border-[#D97706]">
                      Manual review
                    </button>
                    <button data-testid="order-cancel" onClick={quickCancel}
                      className="border border-[#E54D2E] text-[#E54D2E] px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#E54D2E] hover:text-white transition-colors">
                      Cancel order
                    </button>
                  </div>
                  {sel.payment.confirmed_by && (
                    <p className="font-mono2 text-[10px] text-[#8A8F95] mt-2">Confirmed by {sel.payment.confirmed_by} at {fmtDate(sel.payment.confirmed_at)}</p>
                  )}
                </div>
              )}
              <Field label="Order status">
                <select data-testid="order-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp}>
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>)}
                </select>
                {['cancelled', 'refunded'].includes(form.status) && sel.stock_reduced && !sel.stock_restored && (
                  <p className="text-[11px] text-[#1F8A58] mt-1.5">Stock will be restored to inventory on save.</p>
                )}
              </Field>
              <Field label="Tracking reference"><input data-testid="order-tracking-input" value={form.tracking} onChange={(e) => setForm({ ...form, tracking: e.target.value })} className={inp} placeholder="e.g. Royal Mail ref" /></Field>
              <Field label="Internal note (private)"><textarea data-testid="order-note-input" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inp} /></Field>
              {(sel.internal_notes || []).length > 0 && (
                <div className="border hairline p-3 max-h-32 overflow-y-auto space-y-1.5">
                  {sel.internal_notes.map((n, i) => (
                    <p key={i} className="text-[12px] text-[#575B60]"><span className="font-mono2 text-[10px] text-[#8A8F95]">{fmtDate(n.at)} {n.by}:</span> {n.text}</p>
                  ))}
                </div>
              )}
              <button data-testid="order-save" onClick={save} disabled={saving}
                className="w-full bg-[#0B0F12] text-white py-3 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : 'Update order'}
              </button>
              <p className="text-[11px] text-[#8A8F95]">Setting status to “Dispatched” emails the customer automatically (with tracking if provided).</p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
